import React, { useMemo } from 'react';
import { formasPagoList, tiposFacturacionList } from '../../../data/listasDesplegables';
import { PercentInput } from '../../../utils/FormInputs';

// Importamos el listado centralizado de marcas admitidas (Reutilización Senior)
import { MARCAS_TARJETA_ADMITIDAS } from '../../../utils/paymentService';
import { sortAlphabetically } from '../../../firebase/cleanPayload';

/**
 * SectionFacturacion - Componente modular para el control de formas de pago, cuotas, 
 * primas, comisiones y cobranzas primarias de Patrimoniales.
 * Optimizado con consumo de marcas dinámicas e inputs atómicos.
 */
const SectionFacturacion = ({ state, actions }) => {
    const { formData, riskFlags, billingLogic, listaMedios, dbMasters } = state;
    const { 
        handleChange, 
        handleCurrencyChange, 
        handleFormaPagoChange, 
        handleTipoFacturacionChange, 
        handleMonedaChange,
        setFormData 
    } = actions;

    // --- EVALUACIÓN INSENSITIVE DE CBU (Soporte robusto a CBU, Adelanto y Cbu, etc.) ---
    const esCbu = useMemo(() => {
        return String(formData.formaPago || '').toUpperCase().includes('CBU');
    }, [formData.formaPago]);

    // --- ORDENAMIENTO EN MEMORIA EFICIENTE (useMemo) ---
    const sortedFormasPago = useMemo(() => {
        return [...(formasPagoList || [])].sort((a, b) => a.localeCompare(b));
    }, []);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    // --- MEJORA: Memorizar y ordenar alfabéticamente las marcas importadas del servicio ---
    const sortedMarcasAdmitidas = useMemo(() => {
        return sortAlphabetically(MARCAS_TARJETA_ADMITIDAS || []);
    }, []);

    // --- MEJORA MANUAL COMISIÓN: Manejador específico para detener el loop del useEffect en usePolicyLogic ---
    const handleComisionInputChange = (e) => {
        const val = e.target.value;
        setFormData(prev => ({
            ...prev,
            comision: val,
            manualComisionOverride: true // Rompe el ciclo del useEffect automático
        }));
    };

    return (
        <fieldset className="border border-green-200 rounded-xl p-6 bg-green-50/20 text-left shadow-sm animate-fade-in transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-green-800 tracking-tight">Facturación y Valores</legend>
            
            {/* FILA 1: PAGO Y MEDIO (SIEMPRE VISIBLES) */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2 items-end">
                <div>
                    <label className="label-form text-slate-500 font-bold mb-1 block">Forma de Pago *</label>
                    <select 
                        name="formaPago"
                        value={formData.formaPago || ''} 
                        onChange={(e) => handleFormaPagoChange(e.target.value)} 
                        className="input-round border-slate-200 focus:border-green-400 focus:ring-1 focus:ring-green-100 transition-all duration-200"
                        required
                    >
                        <option value="">Seleccione...</option>
                        {sortedFormasPago.map(f => (
                            <option key={f} value={f}>
                                {f}
                            </option>
                        ))}
                    </select>
                </div>

                {billingLogic.permiteDetalle && (
                    <div className="md:col-span-2 animate-fade-in">
                        <label className="label-form text-orange-700 font-bold mb-1 block">Medio de Cobro Registrado</label>
                        <select 
                            name="medioPago"
                            value={formData.medioPago || ''} 
                            onChange={handleChange}
                            className="input-round border-orange-200 font-bold text-orange-800 bg-white focus:border-orange-400 focus:ring-1 focus:ring-orange-100 transition-all duration-200"
                        >
                            <option value="">-- Seleccionar Medio Guardado --</option>
                            {(listaMedios || []).map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
                            <option value="NEW" className="text-blue-600 font-black">+ REGISTRAR NUEVO MEDIO...</option>
                        </select>
                    </div>
                )}
            </div>

            {/* ALTA RÁPIDA DE NUEVO MEDIO DE PAGO */}
            {billingLogic.permiteDetalle && formData.medioPago === 'NEW' && (
                <div className="mt-4 p-5 bg-white border-2 border-dashed border-blue-200 rounded-2xl grid grid-cols-1 md:grid-cols-4 gap-4 animate-fade-in shadow-inner">
                    <p className="col-span-full text-[10px] font-black text-blue-500 uppercase tracking-widest pl-1 select-none">Información del Nuevo Medio</p>
                    
                    {esCbu ? (
                        <>
                            <div className="md:col-span-2 text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Banco Emisor</label>
                                <input type="text" name="nuevoBanco" className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" value={formData.nuevoBanco || ''} onChange={handleChange} placeholder="Ej: Galicia, Santander..." />
                            </div>
                            <div className="md:col-span-2 text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">CBU (22 Dígitos)</label>
                                <input type="text" name="nuevoCbu" className="input-round font-mono border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" value={formData.nuevoCbu || ''} onChange={(e) => setFormData(p => ({ ...p, nuevoCbu: e.target.value.replace(/\D/g, '') }))} maxLength={22} placeholder="0000000000000000000000" />
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Cambio estratégico: select dinámico conectado al centralizador */}
                            <div className="text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Marca</label>
                                <select name="nuevaMarca" className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 font-bold" value={formData.nuevaMarca || ''} onChange={handleChange}>
                                    <option value="">Seleccione...</option>
                                    {sortedMarcasAdmitidas.map(m => (
                                        <option key={m} value={m}>{m}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Banco</label>
                                <input type="text" name="nuevoBanco" className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" value={formData.nuevoBanco || ''} onChange={handleChange} placeholder="Ej: Galicia..." />
                            </div>
                            <div className="text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Número</label>
                                <input type="text" name="nuevoNumero" className="input-round font-mono border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" value={formData.nuevoNumero || ''} onChange={(e) => setFormData(p => ({ ...p, nuevoNumero: e.target.value.replace(/\D/g, '') }))} maxLength={16} placeholder="XXXX XXXX XXXX XXXX" />
                            </div>
                            <div className="text-left">
                                <label className="block text-[10px] font-bold text-slate-400 uppercase mb-1 text-center">Vto</label>
                                <input type="text" name="nuevoVto" className="input-round text-center border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" value={formData.nuevoVto || ''} onChange={handleChange} placeholder="MM/AA" />
                            </div>
                        </>
                    )}
                </div>
            )}

            {/* SEGUNDA FILA: VALORES DE PRIMA, CUOTAS Y COMISIÓN */}
            <div className="flex flex-wrap md:flex-nowrap gap-4 pt-6 items-end">
                <div className="w-full md:w-44 shrink-0">
                    <label className="label-form text-slate-500 font-bold mb-1 block">Tipo Facturación *</label>
                    <select 
                        name="tipoFacturacion"
                        value={formData.tipoFacturacion || ''} 
                        onChange={(e) => handleTipoFacturacionChange(e.target.value)} 
                        className="input-round border-slate-200 focus:border-green-400 focus:ring-1 focus:ring-green-100 transition-all duration-200"
                        required
                    >
                        <option value="">Seleccione...</option>
                        {(tiposFacturacionList || []).map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </div>

                {billingLogic.mostrarCuotas && (
                    <div className="w-full md:w-20 shrink-0 animate-fade-in">
                        <label className="label-form text-slate-500 font-bold mb-1 block">Cuotas</label>
                        <input 
                            type="number" 
                            name="cuotas" 
                            value={formData.cuotas || ''} 
                            onChange={handleChange} 
                            className="input-round border-slate-200 font-bold text-blue-800 text-center focus:border-green-400 focus:ring-1 focus:ring-green-100 transition-all duration-200" 
                            placeholder="0" 
                        />
                    </div>
                )}

                {billingLogic.showMainPremiumFields && (
                    <>
                        {/* Prima Emitida con Selección de Moneda integrada */}
                        <div className="flex-grow min-w-[180px] animate-fade-in text-left">
                            <label className="label-form text-slate-500 font-bold mb-1 block">Moneda / Prima Emitida *</label>
                            <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-100 transition-all">
                                <div className="bg-slate-50 px-2 h-full flex items-center border-r border-slate-100 shrink-0 select-none">
                                    <select 
                                        value={formData.moneda === 'USD' ? 'USD U$S' : 'ARS $'} 
                                        onChange={(e) => handleMonedaChange(e.target.value)} 
                                        className="bg-transparent font-bold text-slate-500 outline-none cursor-pointer uppercase text-[10px]"
                                    >
                                        <option value="ARS $">ARS $</option>
                                        <option value="USD U$S">USD U$S</option>
                                    </select>
                                </div>
                                <input 
                                    type="text" 
                                    value={formData.primaEmitida || ''} 
                                    onChange={(e) => handleCurrencyChange(e, 'primaEmitida')} 
                                    className="w-full px-3 outline-none font-mono font-bold text-slate-700 placeholder:text-slate-300 h-full text-left" 
                                    placeholder="0,00" 
                                    required 
                                />
                            </div>
                        </div>

                        {/* Premio con Símbolo Dinámico */}
                        <div className="flex-grow min-w-[160px] animate-fade-in text-left">
                            <label className="label-form text-slate-500 font-bold mb-1 block">Premio *</label>
                            <div className="flex items-center border border-slate-200 rounded-xl overflow-hidden h-[46px] bg-white focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-100 transition-all">
                                <div className="bg-slate-50 px-3 h-full flex items-center border-r border-slate-100 font-bold text-slate-400 text-xs shrink-0 select-none">
                                    {formData.moneda === 'USD' ? 'U$S' : '$'}
                                </div>
                                <input 
                                    type="text" 
                                    value={formData.premio || ''} 
                                    onChange={(e) => handleCurrencyChange(e, 'premio')} 
                                    className="w-full px-3 outline-none font-mono font-bold text-blue-900 placeholder:text-slate-300 h-full text-left" 
                                    placeholder="0,00" 
                                    required 
                                />
                            </div>
                        </div>
                    </>
                )}

                {/* Prima de la Primera Cuota (Lectura) */}
                {billingLogic.showPrimeraCuota && (
                    <div className="flex-grow min-w-[160px] animate-fade-in text-left">
                        <label className="label-form font-bold text-blue-700 mb-1 block">Prima Primera cuota</label>
                        <div className="flex items-center border border-blue-100 rounded-xl overflow-hidden h-[46px] bg-blue-50/20">
                            <div className="bg-blue-50/50 px-3 h-full flex items-center border-r border-blue-100 font-bold text-blue-700 text-xs shrink-0 select-none">
                                {formData.moneda === 'USD' ? 'U$S' : '$'}
                            </div>
                            <input 
                                type="text" 
                                value={formData.primaPrimeraCuota || ''} 
                                readOnly 
                                className="w-full px-3 outline-none font-mono font-black text-blue-800 cursor-not-allowed h-full text-left bg-transparent" 
                                placeholder="0,00" 
                            />
                        </div>
                    </div>
                )}

                {/* Prima Adelanto (Lectura) */}
                {billingLogic.showAdelanto && (
                    <div className="flex-grow min-w-[160px] animate-fade-in text-left">
                        <label className="label-form font-bold text-emerald-700 mb-1 block">Prima Adelanto</label>
                        <div className="flex items-center border border-emerald-100 rounded-xl overflow-hidden h-[46px] bg-emerald-50/20">
                            <div className="bg-emerald-50/50 px-3 h-full flex items-center border-r border-emerald-100 font-bold text-emerald-700 text-xs shrink-0 select-none">
                                {formData.moneda === 'USD' ? 'U$S' : '$'}
                            </div>
                            <input 
                                type="text" 
                                value={formData.primaAdelanto || ''} 
                                readOnly 
                                className="w-full px-3 outline-none font-mono font-black text-emerald-800 cursor-not-allowed h-full text-left bg-transparent" 
                                placeholder="0,00" 
                            />
                        </div>
                    </div>
                )}

                {/* % Comisión (PercentInput Atómico con interceptor para habilitar edición manual) */}
                <div className="w-full md:w-24 shrink-0">
                    <PercentInput 
                        label="% Comis."
                        name="comision"
                        value={formData.comision}
                        onChange={handleComisionInputChange}
                        textColor="text-blue-800"
                        focusColor="focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-100"
                        placeholder="0,00"
                    />
                </div>
            </div>

            {/* REGISTRO DE COBRO EN PRIMERA CUOTA (TRANSFERENCIA / CUPONES / ADELANTO) */}
            {billingLogic.requiereRegistroCobro && (
                <div className="mt-8 pt-6 border-t border-green-100 flex flex-col gap-4 animate-fade-in">
                    <div className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${
                        formData.pagoEfectuado ? 'bg-blue-50 border-blue-200' : 'bg-white border-slate-100 hover:bg-slate-50'
                    }`}>
                        <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${
                                formData.pagoEfectuado ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-300'
                            }`}>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                </svg>
                            </div>
                            <span className={`text-xs font-black uppercase tracking-wide transition-colors duration-300 ${
                                formData.pagoEfectuado ? 'text-blue-900' : 'text-slate-400'
                            }`}>
                                ¿Pago Efectuado?
                            </span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer scale-110 select-none">
                            <input 
                                type="checkbox" 
                                name="pagoEfectuado" 
                                checked={formData.pagoEfectuado || false} 
                                onChange={handleChange} 
                                className="sr-only peer" 
                            />
                            <div className="w-10 h-5 bg-slate-200 rounded-full relative transition-colors peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-5 shadow-sm"></div>
                        </label>
                    </div>

                    {formData.pagoEfectuado && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in p-4 bg-blue-50/10 border border-dashed border-blue-100 rounded-xl">
                            <div>
                                <label className="block text-sm font-bold text-blue-700 mb-1">¿Quién Cobró?</label>
                                <select 
                                    name="quienCobro" 
                                    value={formData.quienCobro || ''} 
                                    onChange={handleChange} 
                                    className="input-round border-blue-200 bg-white focus-border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200 text-slate-700 font-semibold"
                                >
                                    <option value="">Seleccione Colaborador...</option>
                                    {sortedColaboradores.map(c => (
                                        <option key={c.id} value={c.nombre}>
                                            {c.nombre}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-blue-700 mb-1">Fecha de Cobro</label>
                                <input 
                                    type="date" 
                                    name="fechaCobro" 
                                    value={formData.fechaCobro || ''} 
                                    onChange={handleChange} 
                                    className="input-round border-blue-200 bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100 transition-all duration-200 text-slate-700 font-bold" 
                                />
                            </div>
                        </div>
                    )}
                </div>
            )}
        </fieldset>
    );
};

export default SectionFacturacion;